'use client';

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter, Link } from '../../../i18n/routing';
import { useAuthStore } from '@/stores/auth';
import { Eye, EyeOff } from 'lucide-react';
import { getFullUrl, isValidReturnUrl } from '@/lib/redirect';

/** Sign in with Apple: planned for a future request; set true when backend and env are ready. */
const SIGN_IN_WITH_APPLE_ENABLED = false;

// Icons
const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.52 12.29c0-.85-.08-1.68-.22-2.48H12v4.69h6.46c-.28 1.49-1.12 2.75-2.38 3.59v2.99h3.85c2.25-2.07 3.55-5.12 3.55-8.79z" fill="#4285F4"/>
    <path d="M12 24c3.24 0 5.96-1.07 7.94-2.9l-3.85-2.99c-1.07.72-2.44 1.15-4.09 1.15-3.13 0-5.78-2.11-6.73-4.96H1.31v3.12C3.31 21.37 7.37 24 12 24z" fill="#34A853"/>
    <path d="M5.27 14.29c-.24-.72-.37-1.49-.37-2.29s.13-1.57.37-2.29V6.59H1.31C.47 8.24 0 10.07 0 12c0 1.93.47 3.76 1.31 5.41l3.96-3.12z" fill="#FBBC05"/>
    <path d="M12 4.79c1.76 0 3.34.61 4.59 1.8l3.43-3.43C17.95 1.19 15.24 0 12 0 7.37 0 3.31 2.63 1.31 6.59l3.96 3.12c.95-2.85 3.6-4.96 6.73-4.96z" fill="#EA4335"/>
  </svg>
);

export default function LoginPage() {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const authStore = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [mounted, setMounted] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    show: boolean;
    message: string;
    color: 'success' | 'error' | 'warning' | 'info';
  }>({
    show: false,
    message: '',
    color: 'info',
  });

  // Check for error query parameter on mount
  useEffect(() => {
    setMounted(true);
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const returnUrl = params.get('returnUrl');

      // First check if user is already logged in - if so, redirect to returnUrl or home
      if (authStore.isLoggedIn) {
        const destination = isValidReturnUrl(returnUrl) ? returnUrl! : '/';
        router.push(destination as Parameters<typeof router.push>[0]);
        return;
      }

      const error = params.get('error');
      if (error) {
        const errorMessage = getErrorMessage(error);
        setSnackbar({
          show: true,
          message: errorMessage,
          color: 'error',
        });
        // Clear error from URL but preserve returnUrl
        const loginPath = isValidReturnUrl(returnUrl)
          ? `/login?returnUrl=${encodeURIComponent(returnUrl ?? '')}`
          : '/login';
        router.replace(loginPath as Parameters<typeof router.replace>[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getErrorMessage = (errorCode: string): string => {
    // BFF Pattern: All errors are session-related, no token errors
    const errorMap: Record<string, string> = {
      'login_failed': t('loginPage.loginError'),
      'token_processing_failed': t('loginPage.loginError'), // BFF: Session verification failed
      'google_login_failed': t('loginPage.googleLoginFailed'),
      'apple_login_failed': t('loginPage.appleLoginFailed'),
      'apple_not_available': t('loginPage.appleLoginFailed'),
    };
    return errorMap[errorCode] || t('loginPage.loginError');
  };

  const validateEmail = (value: string): boolean => {
    return !!value && /.+@.+\..+/.test(value);
  };

  const validatePassword = (value: string): boolean => {
    return !!value && value.length >= 6;
  };

  const handleLogin = async () => {
    if (loading) return;

    if (!validateEmail(email)) {
      showSnackbar(t('loginPage.emailInvalid'), 'warning');
      return;
    }

    if (!validatePassword(password)) {
      showSnackbar(t('loginPage.passwordMinLength'), 'warning');
      return;
    }
    
    if (showTwoFactor && !twoFactorCode) {
        showSnackbar(t('loginPage.enterTwoFactorCode'), 'warning');
        return;
    }

    setLoading(true);

    try {
      const { login } = await import('@/actions/auth');
      const result = await login(email, password, showTwoFactor ? twoFactorCode : undefined);

      if (result.requires2FA) {
        setShowTwoFactor(true);
        // If there's an error message (invalid 2FA code), show it; otherwise show info message
        if (result.error) {
          showSnackbar(result.error, 'error');
        } else {
          showSnackbar(result.message || t('loginPage.enterTwoFactorCode'), 'info');
        }
        setLoading(false);
        return;
      }

      if (result.success) {
        showSnackbar(t('loginPage.loginSuccess'), 'success');

        // Give cookies a moment to be set, then fetch user and redirect
        await new Promise(resolve => setTimeout(resolve, 300));

        // Try to fetch user in background (don't wait for it)
        authStore.fetchUser().catch(() => {});

        // Redirect to returnUrl (original page) or dashboard
        const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        const returnUrl = params?.get('returnUrl');
        const destination = isValidReturnUrl(returnUrl) ? returnUrl! : '/dashboard';
        const redirectUrl = getFullUrl(destination);
        window.location.href = redirectUrl;
        return;
      } else {
        const errorMessage = result.error || t('loginPage.loginError');
        showSnackbar(errorMessage, 'error');
      }
    } catch {
      showSnackbar(t('loginPage.loginError'), 'error');
    } finally {
      if (!showTwoFactor) {
        setLoading(false);
      }
    }
  };

  const loginWithGoogle = () => {
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const desiredReturn = params?.get('returnUrl');
    const returnUrl = isValidReturnUrl(desiredReturn) ? desiredReturn! : '/dashboard';
    const googleLoginUrl = `/api/auth/google/login?returnUrl=${encodeURIComponent(returnUrl)}&locale=${encodeURIComponent(locale)}`;
    window.location.href = googleLoginUrl;
  };

  const loginWithApple = () => {
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const desiredReturn = params?.get('returnUrl');
    const returnUrl = isValidReturnUrl(desiredReturn) ? desiredReturn! : '/dashboard';
    const appleLoginUrl = `/api/auth/apple/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    window.location.href = appleLoginUrl;
  };

  const showSnackbar = (message: string, color: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({
      show: true,
      message,
      color,
    });
    // Auto-hide after 4 seconds
    setTimeout(() => {
        setSnackbar(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 font-sans">
      {/* LEFT SIDE: Image with Overlay */}
      <div className="hidden lg:flex relative min-h-screen overflow-hidden">
        <Image
          src="/login-bg.jpg"
          alt="Sailing adventure"
          fill
          priority
          className="object-cover"
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/50" />
        {/* Quote Overlay */}
        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-center leading-tight">
            {t('loginPage.oceanQuote')}
          </h2>
          <p className="text-lg lg:text-xl text-white/90 text-center max-w-md">
            {t('loginPage.heroSubheadline')}
          </p>
        </div>
      </div>

      {/* RIGHT SIDE: Form */}
      <div className="flex flex-col justify-center items-center p-6 sm:p-8 lg:p-12 bg-white dark:bg-slate-50 min-h-screen">
        <div className="w-full max-w-md">
          {/* Mobile Header Image */}
          <div className="lg:hidden relative h-48 mb-8 rounded-xl overflow-hidden">
            <Image
              src="/login-bg.jpg"
              alt="Sailing adventure"
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60" />
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <h2 className="text-2xl font-bold text-white text-center">
                {t('loginPage.oceanQuote')}
              </h2>
            </div>
          </div>

          {/* Form Card */}
          <div className="w-full">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-900 mb-2">
                {t('loginPage.heroHeadline')}
              </h1>
              <p className="text-slate-600 dark:text-slate-600">
                {t('loginPage.heroSubheadline')}
              </p>
            </div>

            {!showTwoFactor ? (
              <>
                {/* OAuth Section */}
                <div className="flex flex-col gap-3 mb-6">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-50 text-blue-700 dark:text-blue-700 text-xs font-bold rounded-full uppercase tracking-wide">
                      {t('loginPage.fastAndSecure')}
                    </span>
                  </div>

                  <button
                    onClick={loginWithGoogle}
                    className="flex items-center justify-center gap-3 py-3 px-4 bg-white dark:bg-white border border-slate-200 dark:border-slate-300 text-slate-700 dark:text-slate-700 font-semibold rounded-xl hover:bg-slate-50 dark:hover:bg-slate-50 transition-all shadow-sm hover:shadow-md text-base"
                  >
                    {mounted && <GoogleIcon />}
                    <span>{t('loginPage.loginWithGoogle')}</span>
                  </button>

                  {SIGN_IN_WITH_APPLE_ENABLED && (
                    <button
                      onClick={loginWithApple}
                      className="flex items-center justify-center gap-3 py-3 px-4 bg-black hover:bg-zinc-800 text-white font-semibold rounded-xl transition-all shadow-sm hover:shadow-md text-base"
                    >
                      {mounted && <AppleIcon />}
                      <span>{t('loginPage.loginWithApple')}</span>
                    </button>
                  )}
                </div>

                {/* Separator */}
                <div className="relative flex items-center mb-6">
                  <div className="flex-grow border-t border-slate-300 dark:border-slate-300"></div>
                  <span className="flex-shrink-0 mx-4 text-xs font-medium text-slate-500 dark:text-slate-500 uppercase tracking-wider">
                    {t('loginPage.orSeparatorLowercase')}
                  </span>
                  <div className="flex-grow border-t border-slate-300 dark:border-slate-300"></div>
                </div>

                {/* Email Form */}
                <div className="flex flex-col gap-5">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-700">
                      {t('loginPage.emailLabel')}
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={handleKeyPress}
                      required
                      autoComplete="email"
                      className="w-full px-4 py-3 bg-white dark:bg-white border border-slate-300 dark:border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-900 dark:text-slate-900 placeholder:text-slate-400"
                    />
                  </div>

                  <div className="space-y-1 relative">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-700">
                      {t('loginPage.passwordLabel')}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={handleKeyPress}
                        required
                        autoComplete="current-password"
                        className="w-full px-4 py-3 bg-white dark:bg-white border border-slate-300 dark:border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all pr-10 text-slate-900 dark:text-slate-900 placeholder:text-slate-400"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-700"
                      >
                        {mounted ? (showPassword ? <EyeOff size={20} /> : <Eye size={20} />) : null}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={handleLogin}
                    disabled={loading}
                    className="w-full py-3 px-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed mt-2 flex items-center justify-center shadow-lg hover:shadow-blue-500/25"
                  >
                    {loading ? (
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : null}
                    {loading ? t('common.loading') : t('loginPage.loginButton')}
                  </button>
                </div>

                <div className="text-center mt-4">
                  <Link
                    href="/forgot-password"
                    className="text-base font-bold text-blue-600 dark:text-blue-600 hover:underline"
                  >
                    {t('loginPage.forgotPassword') || 'Forgot your password?'}
                  </Link>
                </div>

                <p className="text-center text-sm text-slate-600 dark:text-slate-600 mt-4">
                  {t('loginPage.registerPrompt')}{' '}
                  <Link href="/register" className="text-blue-600 dark:text-blue-600 font-bold hover:underline">
                    {t('loginPage.registerButton')}
                  </Link>
                </p>
              </>
            ) : (
              // 2FA View
              <div className="flex flex-col gap-6">
                <div className="text-center mb-2">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-900">{t('loginPage.twoFactorRequired')}</h2>
                  <p className="text-slate-500 dark:text-slate-500 text-sm mt-1">Enter the code from your authenticator app</p>
                </div>
                
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-700">
                    {t('loginPage.twoFactorCodeLabel')}
                  </label>
                  <input
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value)}
                    onKeyDown={handleKeyPress}
                    required
                    autoFocus
                    className="w-full px-4 py-3 bg-white dark:bg-white border border-slate-300 dark:border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-center tracking-widest font-mono text-lg text-slate-900 dark:text-slate-900"
                  />
                </div>
                  
                <button 
                  onClick={handleLogin}
                  className="w-full py-3 px-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg"
                >
                  {t('loginPage.verifyButton')}
                </button>
                  
                <button 
                  onClick={() => {
                    setShowTwoFactor(false);
                    setTwoFactorCode('');
                  }}
                  className="w-full py-2 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-700 transition-colors"
                >
                  {t('loginPage.backToLogin')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Snackbar - Rendered via Portal */}
      {mounted && snackbar.show && createPortal(
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] animate-in fade-in slide-in-from-top-2 duration-300 w-full max-w-sm px-4">
          <div className={`px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center justify-between ${
            snackbar.color === 'error' ? 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-50 dark:text-red-800 dark:border-red-200' :
            snackbar.color === 'success' ? 'bg-green-50 text-green-800 border border-green-200 dark:bg-green-50 dark:text-green-800 dark:border-green-200' :
            snackbar.color === 'warning' ? 'bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-50 dark:text-amber-800 dark:border-amber-200' :
            'bg-blue-50 text-blue-800 border border-blue-200 dark:bg-blue-50 dark:text-blue-800 dark:border-blue-200'
          }`}>
            <span>{snackbar.message}</span>
            <button 
              onClick={() => setSnackbar(prev => ({ ...prev, show: false }))}
              className="ml-2 hover:opacity-70 p-1"
            >
              ✕
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
